import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import VehicleLayout from '../../components/VehicleLayout';
import { useAxios } from '../../utils/hook/useAxios';

const UPLOAD_PRESET = 'r6s4af83';
const CLOUDINARY_URL = 'https://api.cloudinary.com/v1_1/dtrkcpsky/image/upload';

const PHOTO_SLOTS = [
  {
    key: 'principalPhotos',
    label: 'Photo principale',
    sublabel: 'Vue 3/4 avant — ce que les locataires voient en premier',
    icon: 'bi-image',
    tip: 'Format paysage · Arrière-plan neutre · Lumière naturelle',
  },
  {
    key: 'lateralPhotos',
    label: 'Vue latérale',
    sublabel: 'Côté conducteur ou passager pour montrer la longueur du véhicule',
    icon: 'bi-car-front',
    tip: 'Hauteur de hanche · Arrière-plan dégagé',
  },
  {
    key: 'interiorPhotos',
    label: 'Intérieur',
    sublabel: "Tableau de bord et sièges — rassurez le locataire sur l'état",
    icon: 'bi-display',
    tip: 'Siège conducteur · Bien éclairé · Nettoyé',
  },
];

function UploadZone({ slot, value, onChange }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const upload = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image (JPG, PNG, WebP…)');
      return;
    }
    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', UPLOAD_PRESET);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', CLOUDINARY_URL);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        onChange(slot.key, data.secure_url);
        toast.success(`${slot.label} mise à jour`);
      } catch {
        toast.error("Erreur lors de l'upload");
      } finally {
        setUploading(false);
        setProgress(0);
      }
    };

    xhr.onerror = () => {
      toast.error('Erreur réseau');
      setUploading(false);
    };

    xhr.send(formData);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) upload(file);
  };

  return (
    <div className="card border-0 shadow-sm rounded-4 overflow-hidden h-100">
      {/* Header */}
      <div className="px-3 pt-3 pb-2 border-bottom d-flex align-items-center gap-2">
        <div
          className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
          style={{ width: 32, height: 32, background: '#fff3e8' }}
        >
          <i className={`bi ${slot.icon} text-warning`} style={{ fontSize: 15 }}></i>
        </div>
        <div className="overflow-hidden">
          <p className="mb-0 fw-semibold" style={{ fontSize: 14 }}>
            {slot.label}
          </p>
          <p className="mb-0 text-muted" style={{ fontSize: 11 }}>
            {slot.sublabel}
          </p>
        </div>
      </div>

      {/* Drop zone */}
      <div
        className="position-relative"
        style={{ cursor: uploading ? 'wait' : 'pointer' }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && inputRef.current?.click()}
      >
        {value ? (
          <div style={{ position: 'relative' }}>
            <img
              src={value}
              alt={slot.label}
              style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }}
            />
            {/* Overlay on hover */}
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{
                background: dragging ? 'rgba(255,102,0,.35)' : 'rgba(0,0,0,.0)',
                transition: 'background .2s',
              }}
            >
              {dragging && (
                <p className="text-white fw-bold mb-0" style={{ fontSize: 13 }}>
                  <i className="bi bi-cloud-upload me-1"></i>Déposer ici
                </p>
              )}
            </div>
          </div>
        ) : (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{
              height: 200,
              background: dragging ? '#fff3e8' : '#f8f9fa',
              border: `2px dashed ${dragging ? '#ff6600' : '#dee2e6'}`,
              transition: 'all .2s',
            }}
          >
            <i
              className="bi bi-cloud-upload"
              style={{ fontSize: 32, color: dragging ? '#ff6600' : '#adb5bd' }}
            ></i>
            <p className="mb-0 mt-2 text-muted" style={{ fontSize: 13 }}>
              Cliquez ou glissez une photo ici
            </p>
          </div>
        )}

        {/* Upload progress overlay */}
        {uploading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
            style={{ background: 'rgba(255,255,255,.85)' }}
          >
            <div className="spinner-border text-warning mb-2" style={{ width: 28, height: 28 }} />
            <div className="w-75">
              <div className="progress rounded-pill" style={{ height: 6 }}>
                <div
                  className="progress-bar bg-warning"
                  style={{ width: `${progress}%`, transition: 'width .1s' }}
                />
              </div>
            </div>
            <p className="mb-0 mt-1 text-muted" style={{ fontSize: 12 }}>
              {progress}%
            </p>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div
        className="px-3 py-2 d-flex align-items-center justify-content-between border-top"
        style={{ background: '#fafafa' }}
      >
        <p className="mb-0 text-muted" style={{ fontSize: 11 }}>
          <i className="bi bi-lightbulb me-1 text-warning"></i>
          {slot.tip}
        </p>
        {value && (
          <button
            className="btn btn-sm btn-outline-secondary ms-2 flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              inputRef.current?.click();
            }}
            disabled={uploading}
            style={{ fontSize: 12 }}
          >
            <i className="bi bi-pencil me-1"></i>Changer
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="d-none"
        onChange={(e) => e.target.files[0] && upload(e.target.files[0])}
      />
    </div>
  );
}

function EditPhotosVoitures() {
  const axios = useAxios();
  const { vehiculeId } = useParams();
  const [photos, setPhotos] = useState({
    principalPhotos: null,
    lateralPhotos: null,
    interiorPhotos: null,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get('/vehicles/searchbyid/' + vehiculeId)
      .then(({ data }) => {
        if (data) {
          setPhotos({
            principalPhotos: data.principalPhotos || null,
            lateralPhotos: data.lateralPhotos || null,
            interiorPhotos: data.interiorPhotos || null,
          });
        }
      })
      .catch(console.error);
  }, []);

  const handlePhotoChange = (key, url) => {
    setPhotos((prev) => ({ ...prev, [key]: url }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put('/vehicles/' + vehiculeId, photos);
      toast.success('Photos enregistrées avec succès !');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const filled = Object.values(photos).filter(Boolean).length;

  return (
    <VehicleLayout vehicleId={vehiculeId} pageTitle="Photos">
      <ToastContainer />

      {/* Header */}
      <div className="mb-4">
        <h4 className="fw-bold mb-1">Photos du véhicule</h4>
        <p className="text-muted mb-3" style={{ fontSize: 14 }}>
          Des photos de qualité augmentent vos réservations. Ajoutez au moins la photo principale.
        </p>

        {/* Progression */}
        <div className="d-flex align-items-center gap-3">
          <div className="flex-grow-1">
            <div className="progress rounded-pill" style={{ height: 6 }}>
              <div
                className={`progress-bar ${filled === 3 ? 'bg-success' : 'bg-warning'}`}
                style={{ width: `${(filled / 3) * 100}%`, transition: 'width .3s' }}
              />
            </div>
          </div>
          <span className="text-muted flex-shrink-0" style={{ fontSize: 13 }}>
            {filled}/3 photo{filled !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Tips banner */}
      <div
        className="rounded-3 p-3 mb-4 d-flex gap-3 flex-wrap"
        style={{ background: '#fff8f0', border: '1px solid #ffd8b0' }}
      >
        {[
          { icon: 'bi-aspect-ratio', text: 'Format paysage (16:9)' },
          { icon: 'bi-sun', text: 'Lumière naturelle' },
          { icon: 'bi-geo-alt', text: 'Arrière-plan neutre' },
          { icon: 'bi-camera', text: 'Angles recommandés' },
        ].map(({ icon, text }) => (
          <div key={text} className="d-flex align-items-center gap-2">
            <i className={`bi ${icon} text-warning`} style={{ fontSize: 14 }}></i>
            <span style={{ fontSize: 13 }}>{text}</span>
          </div>
        ))}
      </div>

      {/* Photo slots */}
      <div className="row g-3 mb-4">
        {PHOTO_SLOTS.map((slot) => (
          <div key={slot.key} className="col-12 col-md-4">
            <UploadZone slot={slot} value={photos[slot.key]} onChange={handlePhotoChange} />
          </div>
        ))}
      </div>

      {/* Save button */}
      <div className="d-flex align-items-center gap-3">
        <button
          className="btn btn-tertiary btn-lg px-4"
          onClick={handleSave}
          disabled={saving || filled === 0}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Enregistrement…
            </>
          ) : (
            <>
              <i className="bi bi-floppy me-2" />
              Enregistrer
            </>
          )}
        </button>
        {filled === 0 && <p className="text-muted small mb-0">Ajoutez au moins une photo</p>}
      </div>
    </VehicleLayout>
  );
}

export default EditPhotosVoitures;
